// Word-navigation mode (R-keyboard) — the pure core. The gloss popover is
// reachable by mouse/touch drag today (`selection-layer.tsx`), but a keyboard
// reader has no way to select a token at all: `normalizeSelection` only ever
// sees a `window.getSelection()` a pointer produced. This module is the model
// side of the fix — a token-index cursor keyboard commands move — kept
// separate from the DOM side (a real `Selection`/`Range` painted from
// `cursorSpan`'s offsets via `selectOffsets`, `./dom-offsets`) so the keyboard
// path re-enters `normalizeSelection` with the exact same `[start, end)` a
// mouse drag would have produced. One normalizer, one cache key, two ways in.
//
// Everything here is pure and total (functional-programming policy): no DOM,
// no React, no `Date.now()`/randomness, no throwing. `moveCursor` in
// particular must never step outside its `bounds` or invert a span — a
// keyboard reader has no scrollbar or drag gesture to visually catch a bad
// jump, so silent correctness here is the whole feature.
//
// Tokens are assumed sorted and non-overlapping but NOT contiguous (whitespace
// and punctuation between them produce no token — `scripts/ingest/tokenize.ts`)
// and blocks are assumed sorted and non-overlapping (the ingest flattener's
// order, same assumption `convex/model/selection.ts` and `./interleave.ts`
// make of the same arrays).

import type { DocumentModel } from "@bindery/shared/corpus/document";

/** A token-index span into a DocumentModel. `anchor` is fixed while extending; `head` moves. */
export interface TokenCursor {
  anchor: number;
  head: number;
}

export type CursorOp =
  | "nextWord"
  | "prevWord"
  | "nextBlock"
  | "prevBlock"
  | "blockStart"
  | "blockEnd"
  | "extendRight"
  | "extendLeft";

/** Char-offset bounds the cursor may never leave (a mobile inset's extent, or the whole text). */
export interface CursorBounds {
  start: number;
  end: number;
}

// ------------------------------------------------------------- binary search
// All three helpers below exploit the same fact: tokens/blocks are sorted by
// `start`, and (since spans don't overlap) `end` rises with `start` too. So
// "first index with start >= x" and "first index with end > x" are both
// ordinary lower-bound searches over a monotonic predicate.

/** First token index with `start >= x`, or `tokens.length` if none. */
function lowerBoundStart(
  tokens: readonly { start: number }[],
  x: number,
): number {
  let lo = 0;
  let hi = tokens.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (tokens[mid]!.start < x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Last token index with `start <= offset`, or `null` if `offset` precedes every token. */
function lastIndexWithStartAtMost(
  tokens: readonly { start: number }[],
  offset: number,
): number | null {
  let lo = 0;
  let hi = tokens.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (tokens[mid]!.start <= offset) {
      ans = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return ans === -1 ? null : ans;
}

/**
 * The `[lo, hi]` inclusive index range of tokens sitting ENTIRELY inside
 * `bounds` — not merely touching it. `moveCursor` treats this as the whole
 * legal universe: a plain move, a block jump, or an extension all resolve a
 * raw target first and then check it against this range, so bounds-respecting
 * is one piece of logic instead of eight. `null` when no token qualifies (an
 * empty range, or a range with no tokens in it at all).
 */
function boundedRange(
  model: DocumentModel,
  bounds: CursorBounds,
): { lo: number; hi: number } | null {
  const lo = lowerBoundStart(model.tokens, bounds.start);
  if (lo >= model.tokens.length) return null;
  // Last index with `end <= bounds.end` == (first index with `end >
  // bounds.end`) - 1. Searching only from `lo` onward is safe: `end` is
  // monotonic non-decreasing with index, so the transition point can't sit
  // before `lo` without every index from `lo` on already failing too, which
  // this search still discovers correctly (it lands right back at `lo`).
  let l = lo;
  let h = model.tokens.length;
  while (l < h) {
    const mid = (l + h) >>> 1;
    if (model.tokens[mid]!.end <= bounds.end) l = mid + 1;
    else h = mid;
  }
  const hi = l - 1;
  return hi < lo ? null : { lo, hi };
}

/** Index into `model.blocks` of the block containing `offset`, or `null`. */
function blockIndexAt(model: DocumentModel, offset: number): number | null {
  let lo = 0;
  let hi = model.blocks.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (model.blocks[mid]!.start <= offset) {
      ans = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  if (ans === -1) return null;
  const block = model.blocks[ans]!;
  return offset < block.end ? ans : null;
}

/** Token containing `offset`, or `null` if `offset` is in a token or before the first one. */
export function tokenIndexAt(
  model: DocumentModel,
  offset: number,
): number | null {
  const idx = lastIndexWithStartAtMost(model.tokens, offset);
  if (idx === null) return null;
  const token = model.tokens[idx]!;
  return offset < token.end ? idx : null;
}

/**
 * The first token starting at or after `minOffset` and sitting entirely
 * inside `bounds`; `null` if none. This is both a public query (word-nav
 * mode's entry point: "the first word visible past the top of the viewport")
 * and the primitive `nextBlock`/`prevBlock`/`blockStart`/`blockEnd` build on —
 * finding "the first token of block N" is exactly "the first token at or
 * after that block's start."
 */
export function firstTokenInView(
  model: DocumentModel,
  minOffset: number,
  bounds: CursorBounds,
): number | null {
  const range = boundedRange(model, bounds);
  if (!range) return null;
  const idx = lowerBoundStart(model.tokens, Math.max(minOffset, bounds.start));
  return idx >= range.lo && idx <= range.hi ? idx : null;
}

// --------------------------------------------------------- raw op targets
// "Raw" = bounds-unaware. `moveCursor` computes one of these, then clamps the
// result against `boundedRange` in a single place — see its comment. Keeping
// bounds out of these keeps each one a direct statement of the op's meaning.

function rawNextWord(model: DocumentModel, head: number): number | null {
  return head + 1 < model.tokens.length ? head + 1 : null;
}

function rawPrevWord(head: number): number | null {
  return head > 0 ? head - 1 : null;
}

/** First token of `model.blocks[blockIdx]`, or `null` if that block index is out of range or empty. */
function rawFirstTokenOfBlock(
  model: DocumentModel,
  blockIdx: number,
): number | null {
  const block = model.blocks[blockIdx];
  if (!block) return null;
  const idx = lowerBoundStart(model.tokens, block.start);
  const token = model.tokens[idx];
  return token && token.start < block.end ? idx : null;
}

/** Last token of `model.blocks[blockIdx]`, or `null` if that block index is out of range or empty. */
function rawLastTokenOfBlock(
  model: DocumentModel,
  blockIdx: number,
): number | null {
  const block = model.blocks[blockIdx];
  if (!block) return null;
  const idx = lowerBoundStart(model.tokens, block.end) - 1;
  const token = idx >= 0 ? model.tokens[idx] : undefined;
  return token && token.start >= block.start ? idx : null;
}

/** The block index `head`'s token sits in, or `null` if `head` is out of range. */
function headBlockIndex(model: DocumentModel, head: number): number | null {
  const token = model.tokens[head];
  return token ? blockIndexAt(model, token.start) : null;
}

/**
 * First token of the nearest block past `blockIdx` in direction `step` that
 * HAS one, or `null` at the end of the document.
 *
 * The walk is what keeps `nextBlock`/`prevBlock` agreeing with `nextWord`
 * about where the passage ends: a block holding no word token (a rule, an
 * image caption stripped to punctuation) would otherwise report `atEdge` and
 * announce "End of passage." while `nextWord` steps straight over it into the
 * block after — two ops disagreeing about the same boundary, with the reader
 * stuck mid-chapter on the one that gives up. No such block exists in the
 * shipped corpus (0 of 14,661); this is the cheap guard against the first one.
 */
function firstTokenPastBlock(
  model: DocumentModel,
  blockIdx: number,
  step: 1 | -1,
): number | null {
  for (let i = blockIdx + step; i >= 0 && i < model.blocks.length; i += step) {
    const token = rawFirstTokenOfBlock(model, i);
    if (token !== null) return token;
  }
  return null;
}

/** Bounds-unaware target index for every op; `null` when the op has nowhere to go at all. */
function rawTarget(
  model: DocumentModel,
  cursor: TokenCursor,
  op: CursorOp,
): number | null {
  switch (op) {
    case "nextWord":
    case "extendRight":
      return rawNextWord(model, cursor.head);
    case "prevWord":
    case "extendLeft":
      return rawPrevWord(cursor.head);
    case "nextBlock": {
      const blockIdx = headBlockIndex(model, cursor.head);
      return blockIdx === null ? null : firstTokenPastBlock(model, blockIdx, 1);
    }
    case "prevBlock": {
      const blockIdx = headBlockIndex(model, cursor.head);
      return blockIdx === null
        ? null
        : firstTokenPastBlock(model, blockIdx, -1);
    }
    case "blockStart": {
      const blockIdx = headBlockIndex(model, cursor.head);
      return blockIdx === null ? null : rawFirstTokenOfBlock(model, blockIdx);
    }
    case "blockEnd": {
      const blockIdx = headBlockIndex(model, cursor.head);
      return blockIdx === null ? null : rawLastTokenOfBlock(model, blockIdx);
    }
    default: {
      // Exhaustiveness check only — every `CursorOp` variant is handled above,
      // so this never runs. No throw: an unreachable branch that threw would
      // still be a throw if the union ever grew a variant this switch forgot.
      const _exhaustive: never = op;
      return _exhaustive;
    }
  }
}

/**
 * Apply one navigation op. Total: never throws, never returns a cursor
 * outside `bounds`, never inverts a plain-move cursor (`extend*` may, by
 * design — see `cursorSpan`).
 *
 * Plain moves collapse `anchor` to the new `head`. `extendRight`/`extendLeft`
 * hold `anchor` and move `head` only. Every op resolves a bounds-unaware
 * target (`rawTarget`) and then checks it against `boundedRange` in this one
 * place — the single spot that decides "did this leave the bounds," so the
 * eight ops can't drift out of sync on what counts as an edge.
 *
 * `atEdge: true` covers three cases the caller can't tell apart and doesn't
 * need to: there is no such token/block at all (`prevWord` at token 0),
 * `bounds` starts empty (no token sits inside it), or the target exists but
 * falls outside `bounds`. In every case the returned `cursor` is the exact
 * input reference, unchanged.
 */
export function moveCursor(
  model: DocumentModel,
  cursor: TokenCursor,
  op: CursorOp,
  bounds: CursorBounds,
): { cursor: TokenCursor; atEdge: boolean } {
  const range = boundedRange(model, bounds);
  if (!range || !model.tokens[cursor.head]) return { cursor, atEdge: true };

  const raw = rawTarget(model, cursor, op);
  const target =
    raw !== null && raw >= range.lo && raw <= range.hi ? raw : null;
  if (target === null) return { cursor, atEdge: true };

  const extend = op === "extendRight" || op === "extendLeft";
  return {
    cursor: extend
      ? { anchor: cursor.anchor, head: target }
      : { anchor: target, head: target },
    atEdge: false,
  };
}

/**
 * Char offsets `[start, end)` the cursor covers, ascending regardless of
 * direction — `head` may sit before `anchor` after `extendLeft`. This is the
 * exact pair `normalizeSelection`/`selectOffsets` take, which is the point of
 * the whole design: painting a DOM selection from this span and later reading
 * it back through `normalizeSelection` must reproduce it byte for byte.
 *
 * Total: an out-of-range index (a cursor built by hand rather than through
 * `moveCursor`) degrades to a zero-width span rather than throwing.
 */
export function cursorSpan(
  model: DocumentModel,
  cursor: TokenCursor,
): { start: number; end: number } {
  const lo = Math.min(cursor.anchor, cursor.head);
  const hi = Math.max(cursor.anchor, cursor.head);
  const start = model.tokens[lo]?.start ?? 0;
  const end = model.tokens[hi]?.end ?? start;
  return { start, end };
}

/**
 * Human/screen-reader-facing summary of a cursor: the quoted text, which
 * block `head` is in (1-based position in `model.blocks`, headings included —
 * an "line N" announcement for the Odyssey's one-block-per-verse shape), and
 * how many tokens the span covers.
 *
 * `blockOrdinal` defaults to 1 for an out-of-range `head` (see `cursorSpan`):
 * word tokens only ever come from inside block text, so this never happens on
 * a cursor `moveCursor` produced, but a hand-built one must still get a
 * plausible number back rather than `0` or `-1`.
 */
export function describeCursor(
  model: DocumentModel,
  cursor: TokenCursor,
): { quote: string; blockOrdinal: number; tokenCount: number } {
  const span = cursorSpan(model, cursor);
  const quote = model.text.slice(span.start, span.end);

  const lo = Math.min(cursor.anchor, cursor.head);
  const hi = Math.max(cursor.anchor, cursor.head);
  const spanIsRealTokens =
    model.tokens[lo] !== undefined && model.tokens[hi] !== undefined;
  const tokenCount = spanIsRealTokens ? hi - lo + 1 : 0;

  const headToken = model.tokens[cursor.head];
  const blockIdx = headToken ? blockIndexAt(model, headToken.start) : null;
  const blockOrdinal = blockIdx === null ? 1 : blockIdx + 1;

  return { quote, blockOrdinal, tokenCount };
}
